import React, { useId } from "react";
import { Box, Typography } from "@mui/material";

const ACCENT = "#6D72F6";
const BORDER = "#D1D5DB";

/**
 * Normalise options to { value, label }. Plain strings are used as both.
 * Exported for tests.
 */
export function normaliseSegmentedOptions(options) {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => (opt != null && typeof opt === "object" ? opt : { value: opt, label: opt }))
    .filter((opt) => opt.value != null && opt.label != null && opt.label !== "");
}

/**
 * Single-select segmented toggle inside an outlined frame, with an optional floating
 * label on the frame's top border (same treatment as the page header filters).
 *
 * Each option has the same box as the chart cards' Reset button (MUI small outlined,
 * as overridden there): 12px / 500 text on a 21px line, 3px x 12px padding, 1px border,
 * 6px radius, about 29px tall. The frame is taller; its top inset leaves room for the
 * label so it never touches an option.
 *
 * Props:
 *   options      - array of strings, or { value, label } objects
 *   value        - the selected option's value
 *   onChange     - called with the clicked option's value
 *   label        - optional text on the frame's top border; also the group's name
 *   ariaLabel    - accessible name when there is no visible label
 *   testIdPrefix - each option gets data-testid `${testIdPrefix}-${value}`
 *   height       - optional fixed frame height (border-box, options centred), to
 *                  line up with other controls in a row; omit for the natural size
 */
const SegmentedToggle = ({
  options = [],
  value,
  onChange,
  label,
  ariaLabel,
  testIdPrefix = "segmented-toggle",
  height,
}) => {
  const labelId = useId();
  const items = normaliseSegmentedOptions(options);
  if (items.length === 0) return null;

  return (
    <Box
      role="group"
      aria-labelledby={label ? labelId : undefined}
      aria-label={label ? undefined : ariaLabel}
      sx={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px",
        border: `1px solid ${BORDER}`,
        borderRadius: "8px",
        bgcolor: "#fff",
        pt: label ? "10px" : "6px",
        pb: "6px",
        px: "6px",
        ...(height ? { height, boxSizing: "border-box", py: 0, alignContent: "center" } : {}),
      }}
    >
      {label && (
        <Typography
          id={labelId}
          sx={{
            fontSize: "10px",
            fontWeight: 500,
            lineHeight: "12px",
            color: "#686868",
            position: "absolute",
            top: "-7px",
            left: "10px",
            px: "4px",
            backgroundColor: "#fff",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {label}
        </Typography>
      )}
      {items.map((item) => {
        const isActive = item.value === value;
        return (
          <Box
            key={String(item.value)}
            component="button"
            type="button"
            aria-pressed={isActive}
            data-testid={`${testIdPrefix}-${item.value}`}
            onClick={() => onChange?.(item.value)}
            sx={{
              font: "inherit",
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "21px",
              whiteSpace: "nowrap",
              px: 1.5,
              py: "3px",
              m: 0,
              // Always 1px, transparent when inactive, so every option is the same size.
              border: "1px solid",
              borderColor: isActive ? ACCENT : "transparent",
              borderRadius: "6px",
              cursor: "pointer",
              color: isActive ? ACCENT : "#6B7280",
              bgcolor: isActive ? "#F5F5FF" : "transparent",
              transition: "background-color 0.15s, color 0.15s, border-color 0.15s",
              "&:hover": { bgcolor: "#F5F5FF", color: isActive ? ACCENT : "#444B51" },
              "&:focus-visible": { outline: `2px solid ${ACCENT}`, outlineOffset: "1px" },
            }}
          >
            {item.label}
          </Box>
        );
      })}
    </Box>
  );
};

export default SegmentedToggle;
